import React, { useState, useRef } from 'react';
import {
  Wrapper,
  Heading,
  Subhead,
  Description,
  Form,
  Input,
  Button,
  ButtonGroup,
  SupportText,
  ResendCodeButton,
} from '../../styles/TwilioVerification.styled';
import { checkVerification } from '../../FreeTrialForm/utils/helpers';
import { verifyTwilio } from '../../FreeTrialForm/utils/helpers';
import { formatPhoneNumber } from '../../FreeTrialForm/utils/helpers';

const TwilioForm = ({
  phone,
  email,
  setTwilioVerified,
  verifyType,
  setVerificationStatus,
  setShowTwilioVerification,
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resendStatus, setResendStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const inputRefs = useRef([]);

  const focusNextInput = currentIndex => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < 6) {
      inputRefs.current[nextIndex].focus();
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const phoneFormatted = formatPhoneNumber(phone);
    const verified = await checkVerification(
      verifyType,
      email,
      phoneFormatted,
      code.join('')
    );

    if (verified === 'approved') {
      setTwilioVerified(true);
      setVerificationStatus('approved');
      setCode(['', '', '', '', '', '']);
    } else {
      setTwilioVerified(false);
      setVerificationStatus('canceled');
      setCode(['', '', '', '', '', '']);
    }
  };

  const handleResendVerificationCode = async () => {
    try {
      setResendStatus('sending');
      await verifyTwilio(verifyType, email, formatPhoneNumber(phone));
      setResendStatus('sent');

      // Manually reset the resend status after 3 seconds
      // This is a workaround for the issue where the resend status is not reset automatically
      setTimeout(() => {
        setResendStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Error resending verification code:', error);
      setResendStatus('error');
      setTimeout(() => {
        setResendStatus('idle');
      }, 3000);
    }
  };

  const handleChange = e => {
    const index = parseInt(e.target.id, 10);
    const value = e.target.value;

    setCode(prevCode =>
      prevCode.map((item, idx) => (idx === index ? value : item))
    );

    if (value.length === 1) {
      focusNextInput(index);
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);
    const newCode = [...code];

    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }

    setCode(newCode);

    const nextEmptyIndex = newCode.findIndex(val => !val);
    const targetIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[targetIndex]?.focus();
  };

  return (
    <Wrapper>
      <Heading>{`Verify ${
        verifyType === 'sms' ? 'phone number' : 'email'
      }`}</Heading>
      <Subhead>Enter your verification code</Subhead>
      {verifyType === 'sms' ? (
        <Description>
          {`We sent a 6-digit code to ${
            phone ? phone : 'to the phone number provided'
          }. Confirm it belongs to you to keep
        your account secure.`}
        </Description>
      ) : (
        <Description>
          {`We sent a 6-digit code to ${
            email ? email : 'to the email provided'
          }. Confirm it belongs to you to keep
        your account secure.`}
        </Description>
      )}
      <div>
        <Form id="otp-form" onSubmit={e => handleSubmit(e)}>
          {code?.map((_, idx) => (
            <Input
              type="number"
              id={`${idx}`}
              name={`code-${idx}`}
              min="0"
              max="9"
              onChange={handleChange}
              onPaste={handlePaste}
              value={code[idx]}
              ref={el => {
                inputRefs.current[idx] = el;
              }}
            />
          ))}
        </Form>
        <ResendCodeButton
          onClick={() => handleResendVerificationCode()}
          disabled={resendStatus === 'sending'}
        >
          {resendStatus === 'sending'
            ? 'Sending...'
            : resendStatus === 'sent'
            ? 'Code Sent!'
            : resendStatus === 'error'
            ? 'Error sending code'
            : 'Resend Code'}
        </ResendCodeButton>
      </div>
      <SupportText>
        UpKeep may use your phone number to call or send text messages with
        information regarding your account.
      </SupportText>
      <ButtonGroup>
        <Button onClick={() => setShowTwilioVerification(false)}>Back</Button>
        <Button form="otp-form" type="submit" theme="steel">
          Submit
        </Button>
      </ButtonGroup>
    </Wrapper>
  );
};

export default TwilioForm;
